/**
 * Subdomain-isolated auth storage for Supabase
 * Prevents auth session bleeding across organization subdomains
 */

import { SupportedStorage } from '@supabase/supabase-js';
import { resolveSubdomain } from './subdomain-utils';

class SubdomainAuthStorage implements SupportedStorage {
  private getStorageKey(key: string): string {
    const subdomain = resolveSubdomain();
    return `${subdomain}:${key}`;
  }

  getItem(key: string): string | null {
    const namespacedKey = this.getStorageKey(key);
    const value = localStorage.getItem(namespacedKey);
    console.log('[AUTH-STORAGE] getItem:', { key, namespacedKey, hasValue: !!value });
    return value;
  }

  setItem(key: string, value: string): void {
    const namespacedKey = this.getStorageKey(key);
    console.log('[AUTH-STORAGE] setItem:', { key, namespacedKey });
    localStorage.setItem(namespacedKey, value);
  }

  removeItem(key: string): void {
    const namespacedKey = this.getStorageKey(key);
    console.log('[AUTH-STORAGE] removeItem:', { key, namespacedKey });
    localStorage.removeItem(namespacedKey);
  }
}

export const subdomainAuthStorage = new SubdomainAuthStorage();

/**
 * Clear all auth data for the current subdomain
 */
export const clearSubdomainAuthData = (): void => {
  const subdomain = resolveSubdomain();
  const keysToRemove: string[] = [];
  
  // Find all auth-related keys for this subdomain
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(`${subdomain}:`)) {
      keysToRemove.push(key);
    }
  }
  
  // Remove all found keys
  keysToRemove.forEach(key => {
    console.log('[AUTH-STORAGE] Clearing key:', key);
    localStorage.removeItem(key);
  });
  
  console.log('[AUTH-STORAGE] Cleared', keysToRemove.length, 'auth keys for subdomain:', subdomain);
};

/**
 * Clear ALL auth data across all subdomains (use when fully signing out)
 */
export const clearAllAuthData = (): void => {
  const keysToRemove: string[] = [];
  
  // Find all auth-related keys (those with subdomain prefix pattern)
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.includes(':sb-') || key?.includes('supabase.auth')) {
      keysToRemove.push(key);
    }
  }
  
  keysToRemove.forEach(key => localStorage.removeItem(key));
  console.log('[AUTH-STORAGE] Cleared all auth data:', keysToRemove.length, 'keys');
};
